import React, { useEffect, useState } from "react";
import { apiGet, apiPatch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Unified Alert Preferences card — Phase 6 Fix 1.
 *
 * Replaces the previous two-section layout (CSE / US separate) with ONE
 * card grouping every notification toggle by *behaviour* rather than by
 * market:
 *
 *    PORTFOLIO ALERTS
 *      🇱🇰 Signal changes (CSE)
 *      🇺🇸 Signal changes (US)            ← only if us_stocks_enabled
 *      🇱🇰 Target reached  (CSE)
 *      🇺🇸 Target reached  (US)            ← only if us_stocks_enabled
 *      🇺🇸 Earnings reminders              ← only if us_stocks_enabled
 *
 *    WATCHLIST ALERTS
 *      🇱🇰 Signal changes (CSE)
 *      🇺🇸 Signal changes (US)            ← only if us_stocks_enabled
 *
 *    WEEKLY SUMMARY
 *      📊 Combined CSE + US weekly digest
 *
 * Backed by the same /api/me/alert-preferences endpoint (no schema
 * change). Each toggle reads/writes the same field as before.
 *
 * Visibility rule: US toggles are only rendered when
 * `isAdmin || user.us_stocks_enabled` — preserving the clean
 * CSE-only experience for users who haven't opted into US stocks.
 *
 * Strict separation contract: the card NEVER mixes currencies — it just
 * lists toggles. The actual emails respect the LKR / USD section split
 * documented in `email_templates.combined_weekly_summary_email` and
 * `us_portfolio_*_grouped_email`.
 */

const SIGNAL_MODES = ["all", "off"];

const ToggleRow = ({ id, flag, title, description, checked, onChange, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <div className="flex-1 space-y-0.5">
      <Label htmlFor={id} className="text-sm font-medium cursor-pointer flex items-center gap-1.5">
        {flag && <span className="text-base leading-none">{flag}</span>}
        {title}
      </Label>
      {description && (
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      )}
    </div>
    <Switch
      id={id}
      checked={!!checked}
      onCheckedChange={onChange}
      data-testid={testid}
    />
  </div>
);

const GroupHeading = ({ title, icon }) => (
  <div className="flex items-center gap-2 pt-2 pb-1.5 border-b">
    {icon}
    <h4 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
      {title}
    </h4>
  </div>
);

const modeAsBool = (mode) => mode !== "off";
const boolAsMode = (b) => (b ? "all" : "off");

export const AlertPreferencesCard = () => {
  const { user, isAdmin } = useAuth();
  const showUs = !!(isAdmin || user?.us_stocks_enabled);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await apiGet("/me/alert-preferences");
        setPrefs(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load alert preferences");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setPref = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

  const onSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      // Normalise into the shape the backend expects. Signal-mode keys
      // accept "all"|"major"|"off" but the unified UI exposes only a
      // boolean toggle, so we map true→"all" and false→"off".
      const body = {
        signal_changes: SIGNAL_MODES.includes(prefs.signal_changes) ? prefs.signal_changes : "all",
        target_reached: !!prefs.target_reached,
        weekly_summary: !!prefs.weekly_summary,
        watchlist_signal_changes: !!(prefs.watchlist_signal_changes ?? true),
      };
      if (showUs) {
        body.us_signal_changes = boolAsMode(modeAsBool(prefs.us_signal_changes ?? "all"));
        body.us_target_reached = !!prefs.us_target_reached;
        body.us_earnings_reminders = !!prefs.us_earnings_reminders;
        body.us_watchlist_signal_changes = boolAsMode(modeAsBool(prefs.us_watchlist_signal_changes ?? "all"));
      }
      const res = await apiPatch("/me/alert-preferences", body);
      toast.success("Alert preferences saved");
      if (res?.alert_preferences) {
        setPrefs((p) => ({ ...p, ...res.alert_preferences }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border bg-card" data-testid="alert-preferences-card">
      <CardHeader>
        <CardTitle className="font-display text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" /> Alert Preferences
        </CardTitle>
        <CardDescription>
          One unified place to control every notification across both markets.
          {showUs && (
            <> Currencies are kept strictly separate in every email — 🇱🇰 CSE values are in Rs., 🇺🇸 US values are in USD.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading || !prefs ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences&hellip;
          </div>
        ) : (
          <div className="space-y-5">
            {/* PORTFOLIO ALERTS */}
            <div className="space-y-1" data-testid="alert-pref-group-portfolio">
              <GroupHeading title="Portfolio Alerts" icon={<span className="text-sm">📈</span>} />
              <ToggleRow
                id="cse-signal"
                flag="🇱🇰"
                title="Signal changes (CSE)"
                description="Email when a CSE holding's composite signal flips (e.g. BUY → WATCH)."
                checked={modeAsBool(prefs.signal_changes ?? "all")}
                onChange={(v) => setPref("signal_changes", boolAsMode(v))}
                testid="alert-pref-cse-signal-toggle"
              />
              {showUs && (
                <ToggleRow
                  id="us-signal"
                  flag="🇺🇸"
                  title="Signal changes (US)"
                  description="Email when a US holding's composite signal flips. Always in USD."
                  checked={modeAsBool(prefs.us_signal_changes ?? "all")}
                  onChange={(v) => setPref("us_signal_changes", boolAsMode(v))}
                  testid="alert-pref-us-signal-toggle"
                />
              )}
              <ToggleRow
                id="cse-target"
                flag="🇱🇰"
                title="Target reached (CSE)"
                description="Email when a CSE holding hits our base-case target price (de-duped to once per 7 days)."
                checked={!!prefs.target_reached}
                onChange={(v) => setPref("target_reached", v)}
                testid="alert-pref-cse-target-toggle"
              />
              {showUs && (
                <ToggleRow
                  id="us-target"
                  flag="🇺🇸"
                  title="Target reached (US)"
                  description="Email when a US holding hits its base-case target price. Always in USD."
                  checked={!!prefs.us_target_reached}
                  onChange={(v) => setPref("us_target_reached", v)}
                  testid="alert-pref-us-target-toggle"
                />
              )}
              {showUs && (
                <ToggleRow
                  id="us-earnings"
                  flag="🇺🇸"
                  title="Earnings reminders"
                  description="One day before a US holding or watched stock reports earnings (08:00 SLT). Sourced from yfinance."
                  checked={!!prefs.us_earnings_reminders}
                  onChange={(v) => setPref("us_earnings_reminders", v)}
                  testid="alert-pref-us-earnings-toggle"
                />
              )}
            </div>

            {/* WATCHLIST ALERTS */}
            <div className="space-y-1" data-testid="alert-pref-group-watchlist">
              <GroupHeading title="Watchlist Alerts" icon={<span className="text-sm">⭐</span>} />
              <ToggleRow
                id="cse-watchlist-signal"
                flag="🇱🇰"
                title="Watchlist signals (CSE)"
                description="Email when a CSE stock you're watching (but don't hold) flips signal."
                checked={!!(prefs.watchlist_signal_changes ?? true)}
                onChange={(v) => setPref("watchlist_signal_changes", v)}
                testid="alert-pref-cse-watchlist-toggle"
              />
              {showUs && (
                <ToggleRow
                  id="us-watchlist-signal"
                  flag="🇺🇸"
                  title="Watchlist signals (US)"
                  description="Email when a US stock you're watching (but don't hold) flips signal."
                  checked={modeAsBool(prefs.us_watchlist_signal_changes ?? "all")}
                  onChange={(v) => setPref("us_watchlist_signal_changes", boolAsMode(v))}
                  testid="alert-pref-us-watchlist-toggle"
                />
              )}
            </div>

            {/* WEEKLY SUMMARY */}
            <div className="space-y-1" data-testid="alert-pref-group-weekly">
              <GroupHeading title="Weekly Summary" icon={<span className="text-sm">📊</span>} />
              <ToggleRow
                id="weekly-summary"
                flag="📊"
                title="Weekly portfolio summary"
                description={
                  showUs
                    ? "ONE combined Monday 08:00 SLT email with separate 🇱🇰 CSE (Rs.) and 🇺🇸 US (USD) sections — never mixed."
                    : "Monday 08:00 SLT digest with portfolio value, week change, signals and attention items."
                }
                checked={!!prefs.weekly_summary}
                onChange={(v) => setPref("weekly_summary", v)}
                testid="alert-pref-weekly-toggle"
              />
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button
                onClick={onSave}
                disabled={saving}
                className="gap-2"
                data-testid="alert-pref-save-button"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Save preferences
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertPreferencesCard;
